import React, { useMemo, useState, useRef, useEffect } from "react";
import { useDashboardData } from "./hooks/useDashboardData";
import { useBusinessType } from "./hooks/useBusinessType";
import DashboardLayout from "./DashboardLayout";
import OrderHistory from "./order-history/OrderHistory";
import SetBarChart from "./set-bar-chart/SetBarChart";
import StatsOverview from "./StatsOverview";
import GenerateDailyReport from "./GenerateReport";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import TopProducts from "./insights/Topproducts";
import TopCustomers from "./insights/TopCustomers";
import TopOrders from "./insights/Toporders";
import StaffPerformance from "./StaffPerformance";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const Dashboard: React.FC = () => {
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [reportGenerated, setReportGenerated] = useState(false);

  // Refs
  const dashboardRef = useRef<HTMLDivElement>(null);
  const previousLoadingRef = useRef(false);
  const restoreScrollTimeoutRef = useRef<NodeJS.Timeout>();

  const { sidebarThemeClass } = useUserTheme();

  const businessType = useBusinessType();

  const isSalonBusiness = businessType?.toLowerCase() === "saloon";

  const now = new Date();

  const [selectedMonth, setSelectedMonth] = useState(MONTHS[now.getMonth()]);

  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const formatLocalDate = (date: Date): string => {
    return date.getFullYear() + "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
  };

  const { startDate, endDate } = useMemo(() => {
    const monthIndex = MONTHS.indexOf(selectedMonth);

    const startDateObj = new Date(selectedYear, monthIndex, 1);

    const endDateObj = new Date(selectedYear, monthIndex + 1, 0);

    return {
      startDate: formatLocalDate(startDateObj),
      endDate: formatLocalDate(endDateObj),
    };
  }, [selectedMonth, selectedYear]);

  /** YEAR DATA */
  const {
    dashboardData: yearlyData,
    loading: yearLoading,
    error: yearError,
  } = useDashboardData({
    mode: "year",
    year: selectedYear,
  });

  /** MONTH DATA */
  const {
    dashboardData: monthlyData,
    loading: monthLoading,
    error: monthError,
  } = useDashboardData({
    mode: "month",
    startDate,
    endDate,
  });

  // Staff summary
  const staffSummary = yearlyData?.staff_summary ?? [];

  // Save scroll position
  const saveScrollPosition = () => {
    if (dashboardRef.current) {
      setScrollPosition(dashboardRef.current.scrollTop);
    }
  };

  // Restore scroll position
  useEffect(() => {
    if (previousLoadingRef.current === true && (yearLoading === false || monthLoading === false)) {
      setIsRestoringScroll(true);

      if (restoreScrollTimeoutRef.current) {
        clearTimeout(restoreScrollTimeoutRef.current);
      }

      restoreScrollTimeoutRef.current = setTimeout(() => {
        if (dashboardRef.current && scrollPosition > 0) {
          dashboardRef.current.scrollTop = scrollPosition;

          setIsRestoringScroll(false);
        }
      }, 100);
    }

    previousLoadingRef.current = yearLoading || monthLoading;

    return () => {
      if (restoreScrollTimeoutRef.current) {
        clearTimeout(restoreScrollTimeoutRef.current);
      }
    };
  }, [yearLoading, monthLoading, scrollPosition]);

  const handleYearChange = (increment: number) => {
    saveScrollPosition();

    const currentYear = new Date().getFullYear();

    setSelectedYear((prev) => {
      const newYear = prev + increment;

      return newYear <= currentYear ? newYear : prev;
    });
  };

  const handleMonthChange = (month: string) => {
    saveScrollPosition();
    setSelectedMonth(month);
  };

  const handleYearChangeFromSelector = (year: number) => {
    saveScrollPosition();
    setSelectedYear(year);
  };

  // Track scroll
  useEffect(() => {
    const handleScroll = () => {
      if (dashboardRef.current && !yearLoading && !monthLoading && !isRestoringScroll) {
        setScrollPosition(dashboardRef.current.scrollTop);
      }
    };

    const scrollElement = dashboardRef.current;

    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll);

      return () => {
        scrollElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [yearLoading, monthLoading, isRestoringScroll]);

  return (
    <DashboardLayout businessType={businessType}>
      <div ref={dashboardRef} className="thin-scrollbar p-1 w-full h-[92vh] overflow-y-auto">
        <div className={`shadow-inner rounded-lg ${sidebarThemeClass}`}>
          {/* Header */}
          <div className="p-2 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>

              <p className="text-gray-600 mt-1">Welcome back! Here's what's happening.</p>
            </div>

            <div className="flex items-center gap-4 flex-wrap md:flex-nowrap">
              {reportGenerated && (
                <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2 max-w-md">
                  The report has been sent to your email. For date-wise reports, please visit the Report tab to view or download them.
                </div>
              )}

              <GenerateDailyReport onReportGenerated={() => setReportGenerated(true)} />
            </div>
          </div>

          <div className="pl-3 pr-3 flex flex-col gap-4">
            {/* MONTH STATS */}
            <div className="relative min-h-[200px]">
              {monthLoading ? (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>

                    <p className="text-gray-600 font-medium">Loading monthly data...</p>
                  </div>
                </div>
              ) : monthlyData ? (
                <StatsOverview dashboardData={monthlyData} selectedMonth={selectedMonth} selectedYear={selectedYear} onMonthChange={handleMonthChange} onYearChange={handleYearChangeFromSelector} />
              ) : null}
            </div>

            {/* YEAR STATS */}
            <div className="relative">
              {yearLoading ? (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 rounded-2xl flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>

                    <p className="text-gray-600 font-medium">Loading yearly data...</p>
                  </div>
                </div>
              ) : yearlyData ? (
                <>
                  <OrderHistory dashboardData={yearlyData} businessType={businessType} />

                  <SetBarChart dashboardData={yearlyData} selectedYear={selectedYear} onYearChange={handleYearChange} />
                </>
              ) : null}
            </div>

            {/* TOP INSIGHTS */}
            {!yearLoading && yearlyData && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
                <TopProducts products={yearlyData.top_products ?? []} />

                <TopCustomers customers={yearlyData.top_customers ?? []} />

                <TopOrders orders={yearlyData.top_orders ?? []} businessType={businessType} />
              </div>
            )}

            {/* STAFF PERFORMANCE */}
            {isSalonBusiness && !yearLoading && staffSummary.length > 0 && <StaffPerformance staffData={staffSummary} />}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
